<template>
  <MasterdataTableContainer
    :loading="$apollo.loading"
    :headers="headers"
    :table-data="tableData"
    :pagination="false"
    @row-click="onRowClick"
  >
    <v-menu
      slot="left"
      v-model="dateDialog"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      max-width="290px"
      min-width="290px"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          rounded
          filled
          v-model="dateRangeText"
          dense
          :hide-details="true"
          v-on="on"
          v-bind="attrs"
          readonly
          prepend-inner-icon="mdi-calendar-range"
          class="mr-4"
        ></v-text-field>
      </template>
      <v-date-picker
        first-day-of-week="1"
        v-model="dates"
        range
      ></v-date-picker>
    </v-menu>

    <template v-slot:modal>
      <v-dialog :value="Boolean(selectedDeviation)" width="600" persistent>
        <LinkDeviationDelayModal
          v-if="Boolean(selectedDeviation)"
          :item="selectedDeviation"
          @confirm="onConfirm"
        />
      </v-dialog>
    </template>

    <v-btn
      @click="exportExcel"
      slot="right"
      rounded
      :disabled="!tableData.length"
      :loading="exporting"
    >
      <v-icon left small>mdi-file-export-outline</v-icon>
      Export Excel</v-btn
    >
  </MasterdataTableContainer>
</template>

<script>
import MasterdataTableContainer from '@/modules/masterdata/components/MasterdataTableContainer'
import LinkDeviationDelayModal from './LinkDeviationDelayModal'
import { get } from 'vuex-pathify'
import { format } from 'date-fns'
import orderBy from 'lodash.orderby'
import { exportExcel } from '@/services/exportExcel.service'

export default {
  name: 'LinkDeviationDelayTable',

  components: {
    MasterdataTableContainer,
    LinkDeviationDelayModal
  },

  data() {
    return {
      exporting: false,
      dateDialog: false,
      dates: [
        format(new Date(), 'yyyy-MM-dd'),
        format(new Date(), 'yyyy-MM-dd')
      ],
      headers: [
        {
          text: 'Händelse',
          value: 'delay.event.title'
        },
        {
          text: 'Tågnr.',
          value: 'train_number'
        },
        {
          text: 'Plats',
          value: '_location_place'
        },
        {
          text: 'Annonserad tid',
          value: '_advertised_location_time'
        },
        {
          text: 'Platstid',
          value: '_location_time'
        },
        {
          text: 'Försening (min)',
          value: 'delay_minutes'
        },
        {
          text: 'Level1',
          value: 'level1_code'
        },
        {
          text: 'Level2',
          value: 'level2_code'
        },
        {
          text: 'Level3',
          value: 'level3_code'
        },
        {
          text: 'Orsak',
          value: 'delay_reason_description'
        },
        {
          text: 'Ansvar',
          value: 'responsible'
        },
        {
          text: 'Kommentar',
          value: 'comment'
        },
        {
          text: 'THR',
          value: 'thr'
        },
        {
          text: 'ÖDOC',
          value: 'odoc'
        }
      ]
    }
  },

  watch: {
    dates: {
      handler: 'onConfirm',
      immediate: true
    }
  },

  computed: {
    selectedDeviation: get('linkDeviation/selectedDeviation'),
    stops: get('event/stops'),
    unhandledDelays: get('linkDeviation/unhandledDelays'),

    datesSorted() {
      return orderBy(this.dates, [x => x], ['asc'])
    },

    dateRangeText() {
      return this.dates.join(' ~ ')
    },

    tableData() {
      const findLocationName = signature => {
        const location = this.stops.find(
          x => x.short_name === signature.toUpperCase()
        )
        return location ? location.stop_name : signature.toUpperCase()
      }

      return orderBy(
        (this.unhandledDelays || []).map(x => ({
          ...x,
          _advertised_location_time: format(
            new Date(x.advertised_location_time),
            'yyyy-MM-dd HH:mm'
          ),
          _location_place: findLocationName(x.location_place),
          _location_time: format(new Date(x.location_time), 'yyyy-MM-dd HH:mm')
        })),
        ['advertised_location_time'],
        ['desc']
      )
    }
  },

  methods: {
    onConfirm() {
      if (this.datesSorted.length !== 2) return
      const [from, to] = this.datesSorted
      this.$store.dispatch('linkDeviation/fetchUnhandledDelays', {
        district_id: this.$store.get('app/selectedDistrictId'),
        from,
        to
      })
    },

    onRowClick(item) {
      this.$store.set('linkDeviation/selectedDeviation', item)
    },

    async exportExcel() {
      this.exporting = true

      const pluckKeys = x => {
        return {
          Händelse: x?.delay?.event?.title,
          'Tågnr.': x.train_number,
          Plats: x._location_place,
          'Annonserad tid': x._advertised_location_time,
          Platstid: x._location_time,
          'Försening (min).': x.delay_minutes,
          Level1: x.level1_code,
          Level2: x.level2_code,
          Level3: x.level3_code,
          Orsak: x.delay_reason_description,
          Ansvar: x.responsible,
          Kommentar: x.comment,
          THR: x.thr,
          ÖDOC: x.odoc
        }
      }

      await exportExcel({
        name: `Förseningar ${this.dateRangeText}`,
        sheets: [
          {
            name: 'Förseningar',
            data: this.tableData.map(pluckKeys)
          }
        ]
      })

      this.exporting = false
    }
  }
}
</script>
